/* components/kitchen/menu/DishRow.jsx — строка блюда в меню.
   desktop: grid-строка (drag-handle · название+чипы · FC · цена · выход · действия).
   xs/sm: карточка (название + FC-чип + цена; остальное — по раскрытию/аккордеон, фикс №42).
   Денежная колонка — ЦЕНА ПРОДАЖИ (selling_price_rub из ТТК): меню — про прайс;
   себестоимость живёт в FC-чипе и в «Экономике» Обзора.
   Переупорядочивание: DnD на desktop (handle), кнопки ↑/↓ везде (фикс №43).
   Чипы: стоп-лист / FC-цветокод меню (<25/25–30/≥30) / stub («черновик»). Рецепты
   стабов достраивает фоновая tree-job (F40) — per-dish AI-кнопки в строке нет.
   ХЕНДОФФ → frontend/src/components/kitchen/menu/DishRow.jsx. */

import React from 'react';
import { Button } from '../../ui/Button.jsx';
import { IconButton } from '../../ui/Buttons.jsx';
import { NativeSelect } from '../../ui/Field.jsx';
import { Icon } from '../../ui/Icon.jsx';
import { fmt } from '../../../lib/format.js';
import { menuFcTone } from '../../../lib/uiHelpers.js';

  const { useState } = React;

  // FC%-цветокод позиции меню: пороги привязаны к целевому FC% меню (target), когда он
  // задан, — шкала «vs цель» (P2-04). Без target — фиксированная шкала меню (<25/25–30/≥30).
  function FcChip({ value, target }) {
    const tone = menuFcTone(value, target);
    const cls = 'badge fc-badge fc-badge--menu' + (tone === 'ok' ? ' badge--ok' : tone === 'warn' ? ' badge--warn' : tone === 'bad' ? ' badge--bad' : '');
    return <span className={cls}><span className="fc-badge__key">FC</span><span className="num">{value == null ? '—' : fmt.percent(value)}</span></span>;
  }

  function DishChips({ dish }) {
    return (
      <span className="dish-chips">
        {dish.is_stub && <span className="badge badge--neutral badge--sm">черновик</span>}
      </span>
    );
  }

  function DishRow({ dish, sections, mobile, onOpen, onDelete, onMove, onChangeSection, targetFc, canUp, canDown, drag, isOver }) {
    const [expanded, setExpanded] = useState(false);
    const sectionOpts = [{ value: '', label: 'Без раздела' }, ...(sections || []).map((s) => ({ value: s.id, label: s.title }))];

    const nameBtn = (
      <button type="button" className={'dish-row__name' + (dish.is_stub ? ' is-stub' : '')} onClick={() => dish.ttk_id && onOpen && onOpen(dish.ttk_id)} disabled={!dish.ttk_id}>
        <span className="truncate">{dish.title}</span>
      </button>
    );

    const actions = (
      <div className="dish-row__actions">
        <IconButton icon="trash" size="sm" variant="ghost" tooltip="Убрать из меню" onClick={() => onDelete && onDelete(dish.id)} />
      </div>
    );

    const reorder = (
      <div className="dish-row__reorder">
        <IconButton icon="chevron-up" size="sm" variant="ghost" tooltip="Выше" disabled={!canUp} onClick={() => onMove && onMove(dish.id, -1)} />
        <IconButton icon="chevron-down" size="sm" variant="ghost" tooltip="Ниже" disabled={!canDown} onClick={() => onMove && onMove(dish.id, 1)} />
      </div>
    );

    // ── Мобильная карточка ──
    if (mobile) {
      return (
        <div className={'dish-card' + (isOver ? ' is-over' : '')}>
          <button type="button" className="dish-card__head" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
            <span className="dish-card__head-top">
              <span className="dish-card__title">{dish.title}</span>
              <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={16} className="dish-card__chev text-mute" />
            </span>
            <span className="dish-card__head-meta">
              <FcChip value={dish.food_cost_pct} target={targetFc} />
              <span className="dish-card__cost num">{fmt.money(dish.selling_price_rub)}</span>
            </span>
          </button>
          {expanded && (
            <div className="dish-card__body">
              <DishChips dish={dish} />
              <div className="dish-card__grid">
                <div className="dish-card__field"><span className="t-caption text-dim">Раздел</span>
                  <NativeSelect value={dish.section_id == null ? '' : dish.section_id} options={sectionOpts} onChange={(e) => onChangeSection && onChangeSection(dish.id, e.target.value)} /></div>
                <div className="dish-card__field"><span className="t-caption text-dim">Выход</span><span className="num">{fmt.grams(dish.output_g)}</span></div>
              </div>
              <div className="dish-card__foot">
                {reorder}
                <div className="grow" />
                {actions}
              </div>
              {dish.ttk_id && (
                <button type="button" className="ing-card__open" onClick={() => onOpen && onOpen(dish.ttk_id)}><Icon name="file-text" size={14} />Открыть ТТК</button>
              )}
            </div>
          )}
        </div>
      );
    }

    // ── Десктоп-строка ──
    return (
      <div className={'dish-row' + (isOver ? ' is-over' : '') + (drag && drag.dragging ? ' is-dragging' : '')}
        draggable={drag ? drag.draggable : undefined}
        onDragStart={drag ? drag.onDragStart : undefined}
        onDragOver={drag ? drag.onDragOver : undefined}
        onDrop={drag ? drag.onDrop : undefined}
        onDragEnd={drag ? drag.onDragEnd : undefined}>
        <span className="dish-row__grip" aria-hidden="true"><Icon name="grip" size={16} /></span>
        <div className="dish-row__main">{nameBtn}<DishChips dish={dish} /></div>
        <div className="dish-row__fc"><FcChip value={dish.food_cost_pct} target={targetFc} /></div>
        <div className="dish-row__cost num">{fmt.money(dish.selling_price_rub)}</div>
        <div className="dish-row__out num">{fmt.grams(dish.output_g)}</div>
        {reorder}
        {actions}
      </div>
    );
  }

export { DishRow };
